import { useEffect, useRef, useState, type PointerEvent } from "react";
import type { RTKParticipant } from "@cloudflare/realtimekit";
import { getNameInitials } from "./participantName";

function useParticipantMediaState({ participant }: { participant: RTKParticipant }) {
  const [mediaState, setMediaState] = useState(() => ({
    videoEnabled: participant.videoEnabled,
    screenShareEnabled: participant.screenShareEnabled,
    audioEnabled: participant.audioEnabled,
  }));

  useEffect(() => {
    const sync = () => {
      setMediaState({
        videoEnabled: participant.videoEnabled,
        screenShareEnabled: participant.screenShareEnabled,
        audioEnabled: participant.audioEnabled,
      });
    };

    sync();
    participant.on("videoUpdate", sync);
    participant.on("screenShareUpdate", sync);
    participant.on("audioUpdate", sync);

    return () => {
      participant.off("videoUpdate", sync);
      participant.off("screenShareUpdate", sync);
      participant.off("audioUpdate", sync);
    };
  }, [participant]);

  const hasScreenShare =
    mediaState.screenShareEnabled && Boolean(participant.screenShareTracks?.video);
  const hasCamera = mediaState.videoEnabled && Boolean(participant.videoTrack);
  const hasAudio =
    mediaState.audioEnabled && Boolean(participant.audioTrack);

  return { hasScreenShare, hasCamera, hasAudio };
}

const pendingAudioElements = new Set<HTMLAudioElement>();
const audioUnlockListeners = new Set<() => void>();
let livestreamRemoteAudioUnlocked = false;
let audioUnlockListenerAttached = false;
let detachAudioUnlockListener: (() => void) | null = null;

function flushPendingAudioElements() {
  for (const el of pendingAudioElements) {
    void el.play().catch(() => {});
  }
  pendingAudioElements.clear();
}

export function isIosDeviceNeedingAudioGesture() {
  if (typeof navigator === "undefined") return false;
  const { userAgent, platform, maxTouchPoints } = navigator;
  return (
    /iPad|iPhone|iPod/i.test(userAgent) ||
    (platform === "MacIntel" && maxTouchPoints > 1)
  );
}

export function isLivestreamRemoteAudioUnlocked() {
  return livestreamRemoteAudioUnlocked;
}

export function onLivestreamRemoteAudioUnlocked(listener: () => void) {
  audioUnlockListeners.add(listener);
  return () => {
    audioUnlockListeners.delete(listener);
  };
}

export function unlockLivestreamRemoteAudio() {
  livestreamRemoteAudioUnlocked = true;
  flushPendingAudioElements();
  detachAudioUnlockListener?.();
  for (const listener of audioUnlockListeners) {
    listener();
  }
}

function attachAudioUnlockListener() {
  if (audioUnlockListenerAttached) return;
  audioUnlockListenerAttached = true;

  const unlock = () => {
    unlockLivestreamRemoteAudio();
  };

  document.addEventListener("pointerdown", unlock, { once: true });
  document.addEventListener("keydown", unlock, { once: true });
  detachAudioUnlockListener = () => {
    document.removeEventListener("pointerdown", unlock);
    document.removeEventListener("keydown", unlock);
    audioUnlockListenerAttached = false;
    detachAudioUnlockListener = null;
  };
}

function playMediaElement(el: HTMLMediaElement) {
  void el.play().catch(() => {
    if (el instanceof HTMLAudioElement) {
      pendingAudioElements.add(el);
      if (livestreamRemoteAudioUnlocked) {
        flushPendingAudioElements();
      } else {
        attachAudioUnlockListener();
      }
    }
  });
}

function getParticipantAudioTracks({
  participant,
  hasScreenShare,
}: {
  participant: RTKParticipant;
  hasScreenShare: boolean;
}): MediaStreamTrack[] {
  const tracks: MediaStreamTrack[] = [];

  if (
    hasScreenShare &&
    participant.screenShareEnabled &&
    participant.screenShareTracks?.audio
  ) {
    tracks.push(participant.screenShareTracks.audio);
  }

  if (participant.audioEnabled && participant.audioTrack) {
    tracks.push(participant.audioTrack);
  }

  return tracks;
}

const PIP_MARGIN_PX = 12;

function clampPipTranslate({
  x,
  y,
  containerWidth,
  containerHeight,
  pipWidth,
  pipHeight,
}: {
  x: number;
  y: number;
  containerWidth: number;
  containerHeight: number;
  pipWidth: number;
  pipHeight: number;
}) {
  const minX = -(containerWidth - pipWidth - PIP_MARGIN_PX);
  const maxX = 0;
  const minY = 0;
  const maxY = containerHeight - pipHeight - PIP_MARGIN_PX;
  return {
    x: Math.min(maxX, Math.max(minX, x)),
    y: Math.min(maxY, Math.max(minY, y)),
  };
}

export function LivestreamParticipantVideo({
  participant,
  muted = false,
  className,
  fill = false,
  pipDraggable = false,
}: {
  participant: RTKParticipant;
  muted?: boolean;
  className?: string;
  fill?: boolean;
  pipDraggable?: boolean;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const screenShareVideoRef = useRef<HTMLVideoElement>(null);
  const cameraVideoRef = useRef<HTMLVideoElement>(null);
  const remoteAudioRef = useRef<HTMLAudioElement>(null);
  const pipDragRef = useRef<{
    pointerId: number;
    startX: number;
    startY: number;
    originX: number;
    originY: number;
  } | null>(null);
  const [pipTranslate, setPipTranslate] = useState({ x: 0, y: 0 });
  const name = participant.name?.trim() ?? "";
  const { hasScreenShare, hasCamera, hasAudio } = useParticipantMediaState({
    participant,
  });
  const hasVideo = hasScreenShare || hasCamera;
  const showBoth = hasScreenShare && hasCamera;
  const showDraggablePip = showBoth && pipDraggable;

  useEffect(() => {
    if (!showBoth) {
      setPipTranslate({ x: 0, y: 0 });
      pipDragRef.current = null;
    }
  }, [showBoth]);

  const updatePipTranslateFromPointer = ({
    clientX,
    clientY,
  }: {
    clientX: number;
    clientY: number;
  }) => {
    const drag = pipDragRef.current;
    const container = containerRef.current;
    const pip = cameraVideoRef.current;
    if (!drag || !container || !pip) return;

    const dx = clientX - drag.startX;
    const dy = clientY - drag.startY;
    setPipTranslate(
      clampPipTranslate({
        x: drag.originX + dx,
        y: drag.originY + dy,
        containerWidth: container.clientWidth,
        containerHeight: container.clientHeight,
        pipWidth: pip.offsetWidth,
        pipHeight: pip.offsetHeight,
      }),
    );
  };

  const handlePipPointerDown = (event: PointerEvent<HTMLVideoElement>) => {
    if (!showDraggablePip) return;
    event.preventDefault();
    event.currentTarget.setPointerCapture(event.pointerId);
    pipDragRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      originX: pipTranslate.x,
      originY: pipTranslate.y,
    };
  };

  const handlePipPointerMove = (event: PointerEvent<HTMLVideoElement>) => {
    if (pipDragRef.current?.pointerId !== event.pointerId) return;
    updatePipTranslateFromPointer({
      clientX: event.clientX,
      clientY: event.clientY,
    });
  };

  const endPipDrag = (event: PointerEvent<HTMLVideoElement>) => {
    if (pipDragRef.current?.pointerId !== event.pointerId) return;
    pipDragRef.current = null;
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
  };

  useEffect(() => {
    const el = screenShareVideoRef.current;
    if (!el || !hasScreenShare) return;

    const updateStream = () => {
      const videoTracks: MediaStreamTrack[] = [];

      if (participant.screenShareEnabled && participant.screenShareTracks?.video) {
        videoTracks.push(participant.screenShareTracks.video);
      }

      if (videoTracks.length > 0) {
        el.srcObject = new MediaStream(videoTracks);
        playMediaElement(el);
      } else {
        el.srcObject = null;
      }
    };

    updateStream();
    participant.on("screenShareUpdate", updateStream);

    return () => {
      participant.off("screenShareUpdate", updateStream);
      el.srcObject = null;
    };
  }, [participant, hasScreenShare]);

  useEffect(() => {
    const el = cameraVideoRef.current;
    if (!el || !hasCamera) return;

    const updateStream = () => {
      const tracks: MediaStreamTrack[] = [];

      if (participant.videoEnabled && participant.videoTrack) {
        tracks.push(participant.videoTrack);
      }

      if (tracks.length > 0) {
        el.srcObject = new MediaStream(tracks);
        playMediaElement(el);
      } else {
        el.srcObject = null;
      }
    };

    updateStream();
    participant.on("videoUpdate", updateStream);

    return () => {
      participant.off("videoUpdate", updateStream);
      el.srcObject = null;
    };
  }, [participant, hasCamera]);

  useEffect(() => {
    if (muted) return;

    const audioEl = remoteAudioRef.current;
    if (!audioEl) return;

    const updateStream = () => {
      const audioTracks = getParticipantAudioTracks({ participant, hasScreenShare });

      if (audioTracks.length > 0) {
        audioEl.srcObject = new MediaStream(audioTracks);
        playMediaElement(audioEl);
      } else {
        audioEl.srcObject = null;
        pendingAudioElements.delete(audioEl);
      }
    };

    updateStream();
    participant.on("audioUpdate", updateStream);
    participant.on("screenShareUpdate", updateStream);

    return () => {
      participant.off("audioUpdate", updateStream);
      participant.off("screenShareUpdate", updateStream);
      audioEl.srcObject = null;
      pendingAudioElements.delete(audioEl);
    };
  }, [participant, hasScreenShare, muted]);

  return (
    <div
      ref={containerRef}
      className={`relative overflow-hidden bg-black ${className ?? ""}`}
    >
      {hasScreenShare ? (
        <video
          ref={screenShareVideoRef}
          autoPlay
          playsInline
          muted
          className="h-full w-full object-contain"
        />
      ) : null}
      {hasCamera ? (
        <video
          ref={cameraVideoRef}
          autoPlay
          playsInline
          muted
          style={
            showDraggablePip
              ? { transform: `translate(${pipTranslate.x}px, ${pipTranslate.y}px)` }
              : undefined
          }
          onPointerDown={showDraggablePip ? handlePipPointerDown : undefined}
          onPointerMove={showDraggablePip ? handlePipPointerMove : undefined}
          onPointerUp={showDraggablePip ? endPipDrag : undefined}
          onPointerCancel={showDraggablePip ? endPipDrag : undefined}
          className={
            showBoth
              ? showDraggablePip
                ? "absolute top-3 right-3 z-10 aspect-video w-[min(28%,12rem)] cursor-grab touch-none rounded-lg border border-white/20 object-cover shadow-lg active:cursor-grabbing"
                : "absolute top-3 right-3 z-10 aspect-video w-[min(28%,12rem)] rounded-lg border border-white/20 object-cover shadow-lg"
              : hasVideo
                ? fill
                  ? "h-full w-full object-cover object-center"
                  : "h-full w-full object-contain"
                : "pointer-events-none fixed h-px w-px opacity-0"
          }
        />
      ) : null}
      {!muted ? (
        <audio ref={remoteAudioRef} autoPlay playsInline className="sr-only" />
      ) : null}
      {!hasVideo ? (
        <div className="absolute inset-0 flex items-center justify-center">
          <div
            className="flex h-24 w-24 items-center justify-center rounded-full bg-zinc-800 text-2xl font-semibold text-white sm:h-32 sm:w-32 sm:text-3xl"
            aria-label={name || "Participant"}
          >
            {getNameInitials({ name })}
          </div>
        </div>
      ) : null}
    </div>
  );
}
